'use strict'

/**
 * ColorHarmoniesMath
 * Workflow:
 * - Convert between LAB and LCH.
 * - Generate harmony color samples from a reference LAB color.
 * - Provide HarmonyType helpers to keep IDs and sizes aligned with Android.
 */
class ColorHarmoniesMath {
    /**
     * Convert LAB to LCH.
     * @param {{l:number, a:number, b:number}} labColor
     * @returns {{l:number, c:number, h:number}}
     */
    static cxLABToLCH(labColor) {
        const a = Number(labColor?.a) || 0
        const b = Number(labColor?.b) || 0
        const l = Number(labColor?.l) || 0
        const c = Math.sqrt(a * a + b * b)
        let h = (Math.atan2(b, a) * 180) / Math.PI
        if (h < 0) h += 360
        return { l, c, h }
    }

    /**
     * Convert LCH to LAB.
     * @param {{l:number, c:number, h:number}} lchColor
     * @returns {{l:number, a:number, b:number}}
     */
    static cxLCHToLAB(lchColor) {
        const l = Number(lchColor?.l) || 0
        const c = Number(lchColor?.c) || 0
        const h = (Number(lchColor?.h) || 0) * (Math.PI / 180)
        return {
            l,
            a: c * Math.cos(h),
            b: c * Math.sin(h),
        }
    }

    /**
     * Complementary harmony (2 colors).
     * @param {{l:number, a:number, b:number}} mainColor
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static complement2Harmony(mainColor) {
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const secondColor = {
            h: lchMainColor.h + 180.0,
            c: lchMainColor.c,
            l: lchMainColor.l,
        }
        if (secondColor.h > 360.0) {
            secondColor.h -= 360.0
        }
        const labColor2 = ColorHarmoniesMath.cxLCHToLAB(secondColor)
        return [mainColor, labColor2]
    }

    /**
     * Analogous harmony.
     * @param {{l:number, a:number, b:number}} mainColor
     * @param {number} angle
     * @param {number} numberOfColors
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static harmonyAnalogous(mainColor, angle, numberOfColors) {
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const numberOfColorsPerSide = Math.floor((numberOfColors - 1) / 2.0)
        const angleToUse = Math.max(Math.min(angle, 90.0), 5.0)
        const colors = []

        for (let i = numberOfColorsPerSide; i >= 1; i -= 1) {
            let hue = lchMainColor.h - (angleToUse / numberOfColorsPerSide) * i
            if (hue < 0.0) hue += 360.0
            colors.push(ColorHarmoniesMath.cxLCHToLAB({ l: lchMainColor.l, c: lchMainColor.c, h: hue }))
        }

        colors.push(mainColor)

        for (let i = 1; i <= numberOfColorsPerSide; i += 1) {
            let hue = lchMainColor.h + (angleToUse / numberOfColorsPerSide) * i
            if (hue > 360.0) hue -= 360.0
            colors.push(ColorHarmoniesMath.cxLCHToLAB({ l: lchMainColor.l, c: lchMainColor.c, h: hue }))
        }

        return colors
    }

    /**
     * Monochromatic harmony (vary chroma).
     * @param {{l:number, a:number, b:number}} mainColor
     * @param {number} numberOfColors
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static harmonyMonochromatic(mainColor, numberOfColors) {
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const colors = [mainColor]

        for (let i = numberOfColors - 2; i >= 0; i -= 1) {
            colors.push(
                ColorHarmoniesMath.cxLCHToLAB({
                    l: lchMainColor.l,
                    c: (lchMainColor.c * i) / (numberOfColors - 1),
                    h: lchMainColor.h,
                }),
            )
        }

        return colors
    }

    /**
     * Monochromatic lighter harmony (vary L).
     * @param {{l:number, a:number, b:number}} mainColor
     * @param {number} numberOfColors
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static harmonyMonochromaticLighter(mainColor, numberOfColors) {
        const interval = (100.0 - mainColor.l) / (numberOfColors - 1)
        const colors = [mainColor]

        for (let i = 1; i < numberOfColors; i += 1) {
            colors.push({ l: mainColor.l + interval * i, a: mainColor.a, b: mainColor.b })
        }

        return colors
    }

    /**
     * Monochromatic darker harmony (vary L).
     * @param {{l:number, a:number, b:number}} mainColor
     * @param {number} numberOfColors
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static harmonyMonochromaticDarker(mainColor, numberOfColors) {
        const colors = [mainColor]

        for (let i = numberOfColors - 2; i >= 0; i -= 1) {
            colors.push({
                l: (mainColor.l * i) / (numberOfColors - 1),
                a: mainColor.a,
                b: mainColor.b,
            })
        }

        return colors
    }

    /**
     * Split complementary harmony (3 colors).
     * @param {{l:number, a:number, b:number}} mainColor
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static splitComplements3Harmony(mainColor) {
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const secondColor = {
            h: lchMainColor.h + 150.0,
            c: lchMainColor.c,
            l: lchMainColor.l,
        }
        if (secondColor.h > 360.0) {
            secondColor.h -= 360.0
        }
        const thirdColor = {
            h: lchMainColor.h - 150.0,
            c: lchMainColor.c,
            l: lchMainColor.l,
        }
        if (thirdColor.h < 0.0) {
            thirdColor.h += 360.0
        }
        return [mainColor, ColorHarmoniesMath.cxLCHToLAB(secondColor), ColorHarmoniesMath.cxLCHToLAB(thirdColor)]
    }

    /**
     * Triadic harmony (3 colors).
     * @param {{l:number, a:number, b:number}} mainColor
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static triadic3Harmony(mainColor) {
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const secondColor = {
            h: lchMainColor.h + 120.0,
            c: lchMainColor.c,
            l: lchMainColor.l,
        }
        if (secondColor.h > 360.0) {
            secondColor.h -= 360.0
        }
        const thirdColor = {
            h: lchMainColor.h - 120.0,
            c: lchMainColor.c,
            l: lchMainColor.l,
        }
        if (thirdColor.h < 0.0) {
            thirdColor.h += 360.0
        }
        return [mainColor, ColorHarmoniesMath.cxLCHToLAB(secondColor), ColorHarmoniesMath.cxLCHToLAB(thirdColor)]
    }

    /**
     * Tetradic harmony (4 colors).
     * @param {{l:number, a:number, b:number}} mainColor
     * @param {number} angle
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    static tetrad4Harmony(mainColor, angle) {
        const angleToUse = Math.max(Math.min(angle, 90.0), 5.0)
        const lchMainColor = ColorHarmoniesMath.cxLABToLCH(mainColor)
        const color2 = { l: lchMainColor.l, c: lchMainColor.c, h: lchMainColor.h + angleToUse }
        const color3 = { l: lchMainColor.l, c: lchMainColor.c, h: lchMainColor.h + 180.0 }
        const color4 = { l: lchMainColor.l, c: lchMainColor.c, h: lchMainColor.h + 180.0 + angleToUse }

        if (color2.h > 360.0) color2.h -= 360.0
        if (color3.h > 360.0) color3.h -= 360.0
        if (color4.h > 360.0) color4.h -= 360.0

        return [
            mainColor,
            ColorHarmoniesMath.cxLCHToLAB(color2),
            ColorHarmoniesMath.cxLCHToLAB(color3),
            ColorHarmoniesMath.cxLCHToLAB(color4),
        ]
    }
}

/**
 * HarmonyType
 * Workflow: store harmony metadata and route to the matching math method.
 */
class HarmonyType {
    /**
     * @param {string} id
     * @param {number} size
     * @param {number} ordinal
     */
    constructor(id, size, ordinal) {
        this.id = id
        this.size = size
        this.ordinal = ordinal
    }

    /**
     * @returns {string}
     */
    getId() {
        return this.id
    }

    /**
     * @returns {number}
     */
    getSize() {
        return this.size
    }

    /**
     * @returns {string}
     */
    getResourceName() {
        return this.id.toLowerCase()
    }

    /**
     * Resolve harmony colors based on the current type.
     * @param {{l:number, a:number, b:number}} labColor
     * @returns {Array<{l:number, a:number, b:number}>}
     */
    colors(labColor) {
        switch (this.ordinal) {
            case 0:
                return ColorHarmoniesMath.complement2Harmony(labColor)
            case 1:
                return ColorHarmoniesMath.harmonyAnalogous(labColor, 30.0, 3)
            case 2:
                return ColorHarmoniesMath.harmonyAnalogous(labColor, 30.0, 5)
            case 3:
                return ColorHarmoniesMath.splitComplements3Harmony(labColor)
            case 4:
                return ColorHarmoniesMath.triadic3Harmony(labColor)
            case 5:
                return ColorHarmoniesMath.tetrad4Harmony(labColor, 60.0)
            case 6:
                return ColorHarmoniesMath.tetrad4Harmony(labColor, 90.0)
            case 7:
                return ColorHarmoniesMath.harmonyMonochromatic(labColor, 3)
            case 8:
                return ColorHarmoniesMath.harmonyMonochromatic(labColor, 4)
            case 9:
                return ColorHarmoniesMath.harmonyMonochromatic(labColor, 5)
            case 10:
                return ColorHarmoniesMath.harmonyMonochromaticLighter(labColor, 3)
            case 11:
                return ColorHarmoniesMath.harmonyMonochromaticLighter(labColor, 4)
            case 12:
                return ColorHarmoniesMath.harmonyMonochromaticLighter(labColor, 5)
            case 13:
                return ColorHarmoniesMath.harmonyMonochromaticDarker(labColor, 3)
            case 14:
                return ColorHarmoniesMath.harmonyMonochromaticDarker(labColor, 4)
            case 15:
                return ColorHarmoniesMath.harmonyMonochromaticDarker(labColor, 5)
            default:
                return [labColor]
        }
    }
}

// Enum-like map of available harmonies (order matches Android).
ColorHarmoniesMath.HarmonyType = {
    Complementary: new HarmonyType('Complementary', 2, 0),
    Analogous3: new HarmonyType('Analogous3', 3, 1),
    Analogous5: new HarmonyType('Analogous5', 5, 2),
    SplitComplementary: new HarmonyType('SplitComplementary', 3, 3),
    Triadic: new HarmonyType('Triadic', 3, 4),
    Tetradic: new HarmonyType('Tetradic', 4, 5),
    Square: new HarmonyType('Square', 4, 6),
    Monochromatic3: new HarmonyType('Monochromatic3', 3, 7),
    Monochromatic4: new HarmonyType('Monochromatic4', 4, 8),
    Monochromatic5: new HarmonyType('Monochromatic5', 5, 9),
    MonochromaticLighter3: new HarmonyType('MonochromaticLighter3', 3, 10),
    MonochromaticLighter4: new HarmonyType('MonochromaticLighter4', 4, 11),
    MonochromaticLighter5: new HarmonyType('MonochromaticLighter5', 5, 12),
    MonochromaticDarker3: new HarmonyType('MonochromaticDarker3', 3, 13),
    MonochromaticDarker4: new HarmonyType('MonochromaticDarker4', 4, 14),
    MonochromaticDarker5: new HarmonyType('MonochromaticDarker5', 5, 15),
}

// Returns all harmony types in a stable order.
ColorHarmoniesMath.HarmonyType.values = () => [
    ColorHarmoniesMath.HarmonyType.Complementary,
    ColorHarmoniesMath.HarmonyType.Analogous3,
    ColorHarmoniesMath.HarmonyType.Analogous5,
    ColorHarmoniesMath.HarmonyType.SplitComplementary,
    ColorHarmoniesMath.HarmonyType.Triadic,
    ColorHarmoniesMath.HarmonyType.Tetradic,
    ColorHarmoniesMath.HarmonyType.Square,
    ColorHarmoniesMath.HarmonyType.Monochromatic3,
    ColorHarmoniesMath.HarmonyType.Monochromatic4,
    ColorHarmoniesMath.HarmonyType.Monochromatic5,
    ColorHarmoniesMath.HarmonyType.MonochromaticLighter3,
    ColorHarmoniesMath.HarmonyType.MonochromaticLighter4,
    ColorHarmoniesMath.HarmonyType.MonochromaticLighter5,
    ColorHarmoniesMath.HarmonyType.MonochromaticDarker3,
    ColorHarmoniesMath.HarmonyType.MonochromaticDarker4,
    ColorHarmoniesMath.HarmonyType.MonochromaticDarker5,
]

// Lookup a harmony type by name string.
ColorHarmoniesMath.HarmonyType.valueOf = (name) => {
    if (!name) return null
    return ColorHarmoniesMath.HarmonyType[name] || null
}

export default ColorHarmoniesMath
